import { useVirtualizer } from "@tanstack/react-virtual";
import lodashIsEqual from "lodash/fp/isEqual";
import type React from "react";
import { useRef, useMemo, useEffect, useState } from "react";
import { PaginationType } from "shared/components/pillar-table/PillarTableContext";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";

const COLUMN_WIDTH_SAMPLE_SIZE = 10;

export type PillarTableVirtualRow<RowGeneric = PillarTableSafeAny> = {
  key: string | number;
  index: number;
  value: RowGeneric;
  ref?: (node: Element | null | undefined) => void;
  offset?: number;
  size?: number;
  isColumnWidthReferenceRow?: boolean;
};

export interface UsePillarTableVirtualValuesOptions<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> {
  pagination: boolean | PaginationType;
  tableRowResults: RowGeneric[];
  isTableWithFixedHeight?: boolean;
  isLoading: boolean;
  isFetching: boolean;
  isRefetching?: boolean;
  isFixedTableHeight?: boolean;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  isFetchingPreviousPage: boolean;
  isFetchingNextPage: boolean;
  fetchPreviousPage: () => void;
  fetchNextPage: () => void;
}

export interface UsePillarTableVirtualValuesResult<
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
> {
  tableRef?: React.RefObject<HTMLDivElement>;
  virtualRows: PillarTableVirtualRow<RowGeneric>[];
  totalTableHeight?: number;
  isInfinityTable: boolean;
  isTableWithFixedHeight?: boolean;
}

export const usePillarTableVirtualValues = <
  RowGeneric = PillarTableSafeAny,
  ExtraPropsGeneric = PillarTableSafeAny | undefined,
  InlineEditFormGeneric = PillarTableSafeAny | undefined,
>(
  options: UsePillarTableVirtualValuesOptions<
    RowGeneric,
    ExtraPropsGeneric,
    InlineEditFormGeneric
  >,
): UsePillarTableVirtualValuesResult<
  RowGeneric,
  ExtraPropsGeneric,
  InlineEditFormGeneric
> => {
  const {
    pagination,
    tableRowResults,
    isTableWithFixedHeight: externalIsTableWithFixedHeight,
    isLoading,
    isFetching,
    hasPreviousPage,
    hasNextPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
    fetchPreviousPage,
    fetchNextPage,
  } = options;

  // Infinity scroll pagination and virtualization should only be applied when the appropriate type is passed
  const isInfinityTable = pagination === PaginationType.Infinity;
  // Use fixed table virtualization if the container cannot be found or the table height is set manually
  const isTableWithFixedHeight =
    isInfinityTable && !!externalIsTableWithFixedHeight;

  const containerRef = useRef<HTMLElement | null>(null);
  const tableRef = useRef<HTMLDivElement | null>(null);

  const [scrollMargin, setScrollMargin] = useState(0);

  useEffect(() => {
    const table = tableRef.current;

    if (!table) {
      return;
    }

    const container =
      table.closest(".appmodal-panel-container") ||
      table.closest(".page-content-container") ||
      table.closest(".container-secondary");

    containerRef.current = container as HTMLElement | null;
  }, [tableRef.current]);

  // Calculate scrollMargin only when using container scroll
  useEffect(() => {
    if (isTableWithFixedHeight) {
      setScrollMargin(0);
      return;
    }

    const updateScrollMargin = () => {
      const container = containerRef.current;
      const table = tableRef.current;

      if (!container || !table) {
        return;
      }

      const containerRect = container.getBoundingClientRect();
      const tableRect = table.getBoundingClientRect();

      const offset = tableRect.top - containerRect.top + container.scrollTop;
      setScrollMargin(offset);
    };

    updateScrollMargin();

    const observer = new ResizeObserver(updateScrollMargin);
    if (tableRef.current) {
      observer.observe(tableRef.current);
    }

    return () => observer.disconnect();
  }, [isTableWithFixedHeight, tableRef.current]);

  const virtualizer = useVirtualizer({
    enabled: isInfinityTable,
    count: tableRowResults.length,
    getScrollElement: () =>
      isTableWithFixedHeight ? tableRef.current : containerRef.current,
    estimateSize: () => 50,
    scrollMargin,
  });

  const virtualItems = isInfinityTable ? virtualizer.getVirtualItems() : [];

  const originalVirtualRows: PillarTableVirtualRow<RowGeneric>[] =
    useMemo(() => {
      return tableRowResults.map((value, index) => ({
        key: index,
        index,
        value,
      }));
    }, [tableRowResults]);

  const columWidthReferenceVirtualRows: PillarTableVirtualRow<RowGeneric>[] =
    useMemo(() => {
      if (!isInfinityTable) {
        return [];
      }

      return tableRowResults
        .slice(0, COLUMN_WIDTH_SAMPLE_SIZE)
        .map((value, index) => ({
          key: `${index}-measure-item`,
          index,
          value,
          isColumnWidthReferenceRow: true,
        }));
    }, [isInfinityTable, tableRowResults]);

  const virtualRows: PillarTableVirtualRow<RowGeneric>[] = useMemo(() => {
    if (!isInfinityTable) {
      return [];
    }

    return virtualItems.map((item) => ({
      key: item.key as number,
      index: item.index,
      value: tableRowResults[item.index],
      ref: virtualizer.measureElement,
      offset: item.start - scrollMargin,
      size: item.size,
    }));
  }, [isInfinityTable, virtualItems, tableRowResults, scrollMargin]);

  const totalTableHeight = virtualizer.getTotalSize();

  const isFirstItemReached = virtualItems?.[0] && virtualItems[0].index === 0;
  const isLastItemReached =
    virtualItems?.[virtualItems?.length - 1] &&
    virtualItems[virtualItems.length - 1].index >= tableRowResults.length - 1;

  useEffect(() => {
    if (!isInfinityTable || isLoading || isFetching) {
      return;
    }

    // Fetch the previous page when the user scrolls to the top
    if (isFirstItemReached && hasPreviousPage && !isFetchingPreviousPage) {
      fetchPreviousPage();
    }

    // Fetch the next page when the user scrolls to the bottom
    if (isLastItemReached && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [
    isInfinityTable,
    isLoading,
    isFetching,
    isFirstItemReached,
    isLastItemReached,
    hasPreviousPage,
    hasNextPage,
    isFetchingPreviousPage,
    isFetchingNextPage,
  ]);

  const firstItem = tableRowResults?.[0];
  const prevFirstItemRef = useRef(firstItem);

  useEffect(() => {
    if (
      !isInfinityTable ||
      lodashIsEqual(firstItem, prevFirstItemRef.current)
    ) {
      return;
    }
    prevFirstItemRef.current = firstItem;
    virtualizer.scrollToIndex(0, { align: "start" });
  }, [firstItem]);

  if (!isInfinityTable) {
    return {
      virtualRows: originalVirtualRows,
      isInfinityTable,
    };
  }

  return {
    tableRef,
    virtualRows: [...columWidthReferenceVirtualRows, ...virtualRows],
    totalTableHeight,
    isInfinityTable,
    isTableWithFixedHeight,
  };
};
